import {
  Box,
  BoxProps,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FlexProps,
  Heading,
  IconButton,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  Assessment,
  Bookmarks,
  Dashboard,
  List as ListIcon,
  ListAlt as OrderIcon,
  LoopOutlined,
  People,
  Settings,
  Today as Calendar,
  Undo,
  ViewAgendaOutlined,
  Web,
} from '@material-ui/icons';
import {
  SettingsProvider,
  useSettingsService,
} from '~/common/services/SettingsService';
import { useAuthenticationService } from '~/common/services/AuthenticationService';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { useVerifyToken } from '~/common/api/zeiterfassungServerApi/AuthApi';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import React, { FunctionComponent, useContext } from 'react';
// @ts-expect-error This is a js only library
import { IconFlagDE, IconFlagUK } from 'material-ui-flags';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import { SvgIconTypeMap } from '@material-ui/core';
import theme from '~/common/customTheme/theme';
import { Button, Text, useToast } from '.';
import { Badge } from './Badge';

type TDrawerContext = {
  open: boolean;
  close: () => void;
};
const DrawerContext = React.createContext<TDrawerContext>(
  null as unknown as TDrawerContext,
);

type NavLinkProps = {
  to: string;
  label?: string;
  Icon: OverridableComponent<SvgIconTypeMap<unknown, 'svg'>>;
  batchContent?: string;
  ml?: number;
};

const NavBarItem: React.FC<NavLinkProps> = ({
  to,
  label,
  Icon,
  batchContent,
  ml = 0,
}) => {
  const navigate = useNavigate();
  const { close } = useContext(DrawerContext);

  return (
    <Tag
      justifyContent="space-between"
      size="lg"
      // mb={2}
      w="100%"
      // colorScheme="gray"
      backgroundColor="white"
      ml={ml}
      alignItems="center"
      cursor="pointer"
      onClick={() => {
        close();
        navigate(to);
      }}
    >
      <Flex justify="space-between" alignItems="center">
        <TagLeftIcon as={Icon} />
        <TagLabel>{label}</TagLabel>
      </Flex>
      {batchContent ? <Badge>{batchContent}</Badge> : null}
    </Tag>
  );
};

type HeaderProps = FlexProps & {
  heading: string;
  setTabIndex?: (tabIndex: number) => void;
};

const Header: React.FC<HeaderProps> = ({ heading, mb, ...props }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const languageSettings = useSettingsService();
  const translate = useTranslate();
  const location = useLocation();
  const disabled = location.pathname === '/';
  const navigate = useNavigate();
  const { logout } = useAuthenticationService();

  const toast = useToast({ status: 'error', description: 'generic.error' });

  return (
    <DrawerContext.Provider value={{ open: isOpen, close: onClose }}>
      <Flex mb={mb === -1 ? 10 : mb}>
        <Flex
          as="nav"
          align="center"
          justify="space-between"
          wrap="wrap"
          padding={1}
          top={0}
          position="fixed"
          bg={theme.colors.primary}
          color="white"
          w="100%"
          h={16}
          zIndex={2}
          {...props}
        >
          <Flex align="center">
            <Box mt={2} ml={2} onClick={onOpen} cursor="pointer">
              <svg
                fill="white"
                width="25px"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Menu</title>
                <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z" />
              </svg>
            </Box>
            <Tooltip
              label={translate('header.backButton.tooltip')}
              aria-label={translate('header.backButton.tooltip')}
            >
              <IconButton
                aria-label={translate('header.backButton.tooltip') as string}
                as={Undo}
                onClick={() => {
                  navigate(-1);
                }}
                disabled={disabled}
                isRound
                mt={2}
                ml={1}
              />
            </Tooltip>
          </Flex>
          <StageInfo />
          <Flex align="center" mr={5}>
            <Heading p={0} m={0} as="h1" size="lg" letterSpacing="-.1rem">
              {heading}
            </Heading>
          </Flex>
        </Flex>
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>
              <Heading>Its4-Plan</Heading>
            </DrawerHeader>
            <DrawerBody position="relative">
              <Stack>
                <NavBarItem
                  label={translate('wrapper.navBarItem.dashboard') as string}
                  to={routes.DASHBOARD}
                  Icon={Dashboard}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.orders')}
                  to={routes.ORDERS_V3}
                  Icon={OrderIcon}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.dayplan')}
                  to={routes.DAYPLAN}
                  Icon={Web}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.employees')}
                  to={routes.USERS}
                  Icon={People}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.timetracking')}
                  to={routes.TIME_TACKING}
                  Icon={Calendar}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.specialJobs')}
                  to={routes.SPECIAL_JOBS}
                  Icon={ViewAgendaOutlined}
                />
                <NavBarItem
                  label={translate('einmalAuftraege.nav')}
                  to={routes.EINMAL_AUFTRAEGE}
                  Icon={ViewAgendaOutlined}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.routines')}
                  to={routes.ROUTINES}
                  Icon={LoopOutlined}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.departments')}
                  to={routes.DEPARTMENTS}
                  Icon={Bookmarks}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.projects')}
                  to={routes.PROJECTS_V2}
                  Icon={ListIcon}
                />
                <NavBarItem
                  label={translate('wrapper.navBarItem.analysis')}
                  to={routes.ANALYSIS}
                  Icon={Assessment}
                />
              </Stack>
              <Stack position="absolute" bottom={5} ml={3}>
                <Flex>
                  <Flex>
                    <IconButton
                      as={IconFlagDE as FunctionComponent}
                      onClick={() => {
                        languageSettings
                          .changeSettings({ language: 'de' })
                          .catch(toast);
                      }}
                      aria-label={
                        translate('wrapper.languages.german') as string
                      }
                      bg="white"
                    />
                    <IconButton
                      as={IconFlagUK as FunctionComponent}
                      onClick={() => {
                        languageSettings
                          .changeSettings({ language: 'en' })
                          .catch(toast);
                      }}
                      aria-label={
                        translate('wrapper.languages.english') as string
                      }
                      bg="white"
                    />
                  </Flex>
                  <NavBarItem
                    label={translate('wrapper.navBarItem.settings')}
                    to={routes.SETTINGS}
                    Icon={Settings}
                  />
                </Flex>
                <Flex>
                  <Button
                    onClick={() => logout()}
                    backgroundColor={theme.colors.danger}
                    ml={1}
                    w="93%"
                  >
                    employeeTile.pdf.logout
                  </Button>
                </Flex>
              </Stack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Flex>
    </DrawerContext.Provider>
  );
};

type WrapperProps = BoxProps & {
  variant?: 'small' | 'regular';
  header?: boolean;
  p?: number;
  maxW?: string | number;
  mb?: number;
  setTabIndex?: (tabIndex: number) => void;
};

export const Wrapper: React.FC<WrapperProps> = ({
  variant = 'regular',
  header = true,
  p = 4,
  mb = 10,
  maxW,
  setTabIndex,
  ...props
}) => {
  let width: string | number = variant === 'regular' ? '100vw' : '60vw';
  if (maxW) {
    width = maxW;
  }
  // Automagically logout user if token is invalid, expired or revoked
  useVerifyToken();
  return (
    <SettingsProvider>
      <Box width="100vw" height="100vh">
        {header ? (
          <Header heading="Its4-Plan" mb={mb} setTabIndex={setTabIndex} />
        ) : null}
        <Flex
          direction="column"
          height="100%"
          maxHeight="95vh"
          maxW={maxW ?? '100vw'}
          w={width}
          p={p}
          overflowX="hidden"
          position="relative"
          {...props}
        >
          <Outlet />
        </Flex>
      </Box>
    </SettingsProvider>
  );
};

function StageInfo() {
  const text = (() => {
    if (window.location.hostname.includes('localhost')) return 'LOCALHOST';
    if (window.location.hostname.includes('testing')) return 'TESTING';
    return '';
  })();
  return (
    <Text fontSize="3xl" color="red" fontWeight="bold">
      {text}
    </Text>
  );
}
